import { CompanyFlags, CompanyFlagsMapping } from '@app/models/ebo/organization/company/company-flags.enum';
import { CompanyStatePayrollRate } from '@app/models/ebo/organization/company/company-state-payroll-rate';
import { Hub } from '@app/models/ebo/places/hub/hub';
import { SystemId } from '@config/system-id';
import { assignSafe, SBaseDisplay } from '@summit/core';
import { SummitFormattedText } from '@summit/models';

import { EboObject } from '../../base-classes/ebo-object';
import { Employee } from '../../employee/employee';
import { Address } from '../../places/address';
import { ServiceArea } from '../service-area/service-area';
import { CompanyCredential } from './company-credential';
import { CompanyFile } from './company-file';

export class Company extends EboObject implements SBaseDisplay {
  tenantID: string;
  name: string;
  ein: string;
  abbreviation: string;
  fedExEntityId: string;
  fedExContractId: string;
  fedExDomicileId: number;
  fedExDomicile: Hub;
  authorizedRepresentativeId: string;
  authorizedRepresentative: Employee;
  parameters: CompanyFlags;
  payrollOrder: number;
  payrollRates: Array<CompanyStatePayrollRate>;
  addressId?: string;
  address?: Address;
  _address?: SummitFormattedText;
  files?: CompanyFile[];
  companyCredentials: CompanyCredential[];
  contactName: string;
  phoneNumber: string;
  emailAddress: string;
  serviceAreas: ServiceArea[];

  get parameterSummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.HasFlag(CompanyFlags.External)) response.AddLine(CompanyFlagsMapping[CompanyFlags.External]);
    if (this.HasFlag(CompanyFlags.ProvideAssets)) response.AddLine(CompanyFlagsMapping[CompanyFlags.ProvideAssets]);
    if (this.HasFlag(CompanyFlags.ProvidesTrucks)) response.AddLine(CompanyFlagsMapping[CompanyFlags.ProvidesTrucks]);
    if (this.HasFlag(CompanyFlags.Hidden)) response.AddLine(CompanyFlagsMapping[CompanyFlags.Hidden]);
    if (this.HasFlag(CompanyFlags.ProvidesMaintenance))
      response.AddLine(CompanyFlagsMapping[CompanyFlags.ProvidesMaintenance]);
    return response;
  }
  set parameterSummary(value: SummitFormattedText) {
    return;
  }

  //TODO rework from schema (make sure works)
  get providesAssets(): boolean {
    return this.HasFlag(CompanyFlags.ProvideAssets);
  }
  set providesAssets(value: boolean) {
    this.SetFlag(CompanyFlags.ProvideAssets, value);
  }
  get providesTrucks(): boolean {
    return this.HasFlag(CompanyFlags.ProvidesTrucks);
  }
  set providesTrucks(value: boolean) {
    this.SetFlag(CompanyFlags.ProvidesTrucks, value);
  }
  get providesMaintenance(): boolean {
    return this.HasFlag(CompanyFlags.ProvidesMaintenance);
  }
  set providesMaintenance(value: boolean) {
    this.SetFlag(CompanyFlags.ProvidesMaintenance, value);
  }
  get external(): boolean {
    return this.HasFlag(CompanyFlags.External);
  }
  set external(value: boolean) {
    this.SetFlag(CompanyFlags.External, value);
  }
  get hidden(): boolean {
    return this.HasFlag(CompanyFlags.Hidden);
  }
  set hidden(value: boolean) {
    this.SetFlag(CompanyFlags.Hidden, value);
  }

  constructor(jsonData?: Partial<Company>) {
    super(jsonData);
    if (jsonData) {
      assignSafe(this, jsonData);
      if (jsonData.payrollRates)
        this.payrollRates = jsonData.payrollRates.map((item) => new CompanyStatePayrollRate(item));
      if (jsonData.fedExDomicile) this.fedExDomicile = new Hub(jsonData.fedExDomicile);
      if (jsonData.authorizedRepresentative)
        this.authorizedRepresentative = new Employee(jsonData.authorizedRepresentative);
      if (jsonData.address) {
        this.address = new Address(jsonData.address);
        this._address = this.address.ToMailingFormat();
      }
      if (jsonData.companyCredentials)
        this.companyCredentials = jsonData.companyCredentials.map((item) => new CompanyCredential(item));
      if (jsonData?.files) this.files = jsonData.files.map((file: Partial<CompanyFile>) => new CompanyFile(file));
      if (jsonData.serviceAreas) this.serviceAreas = jsonData.serviceAreas.map((item) => new ServiceArea(item));
    }
    this.payrollRates ??= [];
    this.companyCredentials ??= [];
    this.files ??= [];
    this.serviceAreas ??= [];
  }

  static GetFedExCompany(): Company {
    return new Company({ id: SystemId.FedExCompanyID, name: 'FedEx' });
  }

  toShortString(): string {
    const response = this.name;
    if (this.hidden) {
      response + ` (Deprecated)`;
    }
    return response;
  }

  HasFlag(flag: CompanyFlags): boolean {
    // eslint-disable-next-line no-bitwise
    return flag === (this.parameters & flag);
  }

  SetFlag(flag: CompanyFlags, value: boolean) {
    if (value) {
      // eslint-disable-next-line no-bitwise
      this.parameters |= flag;
    } else {
      // eslint-disable-next-line no-bitwise
      this.parameters &= ~flag;
    }
  }

  private _addressSummary: SummitFormattedText;
  get addressSummary(): SummitFormattedText {
    if (!this._addressSummary && this.address) {
      this._addressSummary = this.address.ToMailingFormat();
    }
    return this._addressSummary;
  }
}
